/**
 * Dispositivos preferidos da pessoa na chamada (câmera, microfone, alto-falante), guardados SÓ neste navegador.
 *
 * Sem isto a sala sempre entra com o padrão do navegador e quem usa outra câmera (webcam externa, câmera virtual) precisa
 * trocá-la em toda chamada. Escolher um dispositivo no menu da sala o torna o preferido; "usar o padrão do navegador" limpa.
 *
 * Como a sala usa a preferência: lida UMA vez ao entrar (`captureOptionsFrom`) e passada ao LiveKit como `{ ideal }`, que cai
 * no dispositivo padrão se o preferido não está mais ligado (câmera desconectada), em vez de falhar e deixar a pessoa sem
 * câmera. Não pode ser recalculada durante a chamada: o LiveKit recria a sala quando as opções mudam.
 *
 * O armazenamento pode não existir (janela privada, dados bloqueados): tudo funciona sem ele, só não lembra.
 */

export const DEVICE_KINDS = Object.freeze(['audioinput', 'videoinput', 'audiooutput']);

export const DEVICE_PREFERENCES_KEY = 'tw_video_device_prefs';

/** O id "default" não é uma escolha: é o próprio padrão do navegador, e guardá-lo seria guardar nada. */
const isChoice = (value) => typeof value === 'string' && value.length > 0 && value !== 'default';

function browserStorage() {
    try {
        return globalThis.localStorage ?? null;
    } catch {
        return null;
    }
}

/**
 * @param {Pick<Storage, 'getItem' | 'setItem'> | null} [storage]
 */
export function createDevicePreferenceStore(storage = browserStorage()) {
    /** @returns {{ audioinput?: string, videoinput?: string, audiooutput?: string }} */
    function read() {
        try {
            const parsed = JSON.parse(storage?.getItem(DEVICE_PREFERENCES_KEY) ?? 'null');
            if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
            return Object.fromEntries(DEVICE_KINDS.filter((kind) => isChoice(parsed[kind])).map((kind) => [kind, parsed[kind]]));
        } catch {
            return {};
        }
    }

    function write(next) {
        try {
            storage?.setItem(DEVICE_PREFERENCES_KEY, JSON.stringify(next));
        } catch {
            /* sem armazenamento: a escolha vale só nesta chamada */
        }
    }

    return {
        read,
        /** Torna `deviceId` o preferido do tipo `kind`. O id "default" limpa a preferência. */
        save(kind, deviceId) {
            if (!DEVICE_KINDS.includes(kind)) return;
            const next = { ...read() };
            if (isChoice(deviceId)) next[kind] = deviceId;
            else delete next[kind];
            write(next);
        },
        clear(kind) {
            const next = { ...read() };
            delete next[kind];
            write(next);
        },
    };
}

export const devicePreferenceStore = createDevicePreferenceStore();

/**
 * As opções do LiveKit para entrar já com os dispositivos preferidos. Sem preferência, `audio` e `video` são só `true`
 * (como sempre) e não há `roomOptions`.
 *
 * @param {{ audioinput?: string, videoinput?: string, audiooutput?: string }} prefs
 * @returns {{ audio: true | { deviceId: { ideal: string } }, video: true | { deviceId: { ideal: string } },
 *   roomOptions: { audioOutput: { deviceId: string } } | undefined }}
 */
export function captureOptionsFrom(prefs) {
    return {
        audio: isChoice(prefs?.audioinput) ? { deviceId: { ideal: prefs.audioinput } } : true,
        video: isChoice(prefs?.videoinput) ? { deviceId: { ideal: prefs.videoinput } } : true,
        roomOptions: isChoice(prefs?.audiooutput) ? { audioOutput: { deviceId: prefs.audiooutput } } : undefined,
    };
}
